//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { Component, type ReactNode } from 'react';

import { Alert, AlertTitle, Box, Typography } from '@mui/material';

interface PanelErrorBoundaryProps {
  children: ReactNode;
}

interface PanelErrorBoundaryState {
  error: Error | null;
}

/**
 * Contains a throw raised while rendering the panel subtree.
 *
 * The panel clients and the host are siblings of the router, so a manifest
 * the plugin validators reject would otherwise escape past the React root:
 * the whole tree unmounts and every route is blank, with no header and no way
 * to sign out.  The rejection stays loud -- it is how a plugin author learns
 * their manifest is wrong -- but it costs the panel feature rather than the
 * application.
 *
 * A class because error catching is exposed only through these lifecycle
 * methods; React has no hook equivalent.
 */
class PanelErrorBoundary
  extends Component<PanelErrorBoundaryProps, PanelErrorBoundaryState> {
  state: PanelErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PanelErrorBoundaryState {
    return { error };
  }

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <Box
        sx={{
          position: 'fixed',
          right: (theme) => theme.spacing(2),
          bottom: (theme) => theme.spacing(2),
          maxWidth: 420,
          zIndex: (theme) => theme.zIndex.snackbar
        }}
      >
        <Alert severity="error">
          <AlertTitle>Panels are unavailable</AlertTitle>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            A plugin surface failed to load. The rest of the application is
            unaffected.
          </Typography>
          {/* The validator's own message names the offending manifest field,
              so it is the one thing a plugin author needs to see. */}
          <Typography
            component="pre"
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
          >
            {error.message}
          </Typography>
        </Alert>
      </Box>
    );
  }
}

export default PanelErrorBoundary;
